import { createContext } from 'react';
import { FieldType, CustomFieldTypesDoc } from '#types/System.types';
import { fieldDataTypes } from '#types/System.FieldTypes.types';

/**
 * Context value interface for field type management.
 * Provides access to both system and custom field types, along with methods
 * to manage custom field type configurations stored in Firestore.
 */
interface FieldTypeContextValue {
    /**
     * Merged field types containing both system and custom field types.
     * System field types are merged with custom field types, with custom types
     * taking precedence for matching field type IDs.
     */
    fieldTypes: Record<string, FieldType>;

    /**
     * Custom field types only, excluding system field types.
     * These are loaded from the Firestore customFieldTypes document.
     */
    customFieldTypes: Record<string, FieldType>;

    /**
     * Loading state indicating whether custom field types are being fetched
     * from Firestore or are currently being updated.
     */
    isLoading: boolean;

    /**
     * Error state containing any error that occurred during loading or
     * updating of custom field types from Firestore.
     */
    error: Error | null;

    /**
     * Updates the custom field types document in Firestore with debouncing
     * to prevent excessive writes. Useful for frequent updates like form inputs.
     * @param updates - Partial custom field types document to merge
     */
    debouncedSetCustomFieldTypes: (updates: Partial<CustomFieldTypesDoc>) => void;

    /**
     * Sets the custom field types document in Firestore, optionally with merge behavior.
     * When merge is false, completely replaces the document.
     * @param data - Partial or complete custom field types document
     * @param options - Configuration options including merge behavior
     */
    setCustomFieldTypes: (data: Partial<CustomFieldTypesDoc>, options?: { merge: boolean }) => void;

    /**
     * Deletes a custom field type from Firestore.
     * @param fieldId The ID of the custom field to delete.
     */
    deleteFieldType: (fieldId: string) => Promise<void>;
}

/**
 * React context for field type management.
 * Provides access to both system and custom field types throughout the application.
 * Custom field types are loaded from Firestore and merged with system field types.
 */
export const FieldTypeContext = createContext<FieldTypeContextValue>({
    fieldTypes: fieldDataTypes,
    customFieldTypes: {},
    isLoading: true,
    error: null,
    debouncedSetCustomFieldTypes: () => {},
    setCustomFieldTypes: () => {},
    deleteFieldType: async () => {},
});

/**
 * Type export for the field type context value interface.
 * Used by components that consume the FieldTypeContext.
 */
export type { FieldTypeContextValue }; 